<script setup lang="ts">
import { useRouter } from 'vue-router';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Badge } from "@/components/ui/badge";

const router = useRouter();

enum ProService {
  YES = 1,
  NO = 0,
}

interface ServiceProps {
  title: string;
  pro: ProService;
  description: string;
  href: string;
}

const serviceList: ServiceProps[] = [
  {
    title: "智能能耗管理系统",
    description:
      "全方位能源监测与优化解决方案，实时监控用电、用水、碳排放数据，助力企业实现节能减排目标。",
    pro: 1,
    href: "/products/energy-management"
  },
  {
    title: "工业自动化控制",
    description:
      "先进的工业4.0自动化控制系统，PLC编程、SCADA系统、远程监控，提升生产效率和产品质量。",
    pro: 1,
    href: "/products/industrial-automation"
  },
  {
    title: "智能仪器仪表",
    description:
      "高精度智能检测设备，精密测量、数据采集、无线传输，为工业生产提供可靠的数据支撑。",
    pro: 0,
    href: "/products/intelligent-instruments"
  },
  {
    title: "环境监测解决方案",
    description:
      "专业环境监测系统，多参数监测、预警系统、数据分析，实时掌握环境质量变化趋势。",
    pro: 1,
    href: "/products/intelligent-instruments"
  },
];

// 处理卡片点击
const handleCardClick = (href: string) => {
  router.push(href);
};
</script>

<template>
  <section id="services" class="services-section">
    <h2 class="services-subtitle">智能产品服务</h2>
    <h2 class="services-title">物联网+AI专业解决方案 · 工业自动化全方位服务</h2>
    <h3 class="services-description">
      从智能仪器仪表到工业自动化控制系统，从AI智能分析到企业级软件定制，
      我们提供完整的物联网+AI解决方案，助力企业数字化转型升级。
    </h3>

    <div class="services-grid">
      <div 
        v-for="{ title, description, pro, href } in serviceList" 
        :key="title"
        @click="handleCardClick(href)"
        class="service-card-wrapper group"
      >
        <Card class="service-card">
          <CardHeader>
            <CardTitle class="service-card-title">
              {{ title }}
              <!-- 跳转箭头图标 -->
              <svg 
                class="service-card-arrow" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="m9 18 6-6-6-6" 
                  stroke="currentColor" 
                  stroke-width="2" 
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                />
              </svg>
            </CardTitle>
            <CardDescription>{{ description }}</CardDescription>
          </CardHeader>
          <Badge v-if="pro === ProService.YES" variant="secondary" class="service-badge">
            高级版
          </Badge>
          
          <!-- 悬停渐变效果 -->
          <div class="service-card-glow"></div>
        </Card>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Services 主要样式 */
.services-section {
  @apply container py-24 sm:py-32;
}

.services-subtitle {
  @apply text-lg text-primary text-center mb-2 tracking-wider;
}

.services-title {
  @apply text-3xl md:text-4xl text-center font-bold mb-4;
}

.services-description {
  @apply md:w-1/2 mx-auto text-xl text-center text-muted-foreground mb-8;
}

.services-grid {
  @apply grid sm:grid-cols-2 lg:grid-cols-2 gap-4 w-full lg:w-[60%] mx-auto;
}

.service-card-wrapper {
  @apply cursor-pointer transition-transform duration-300 hover:scale-105;
}

.service-card {
  @apply bg-muted/60 dark:bg-card h-full relative overflow-hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
}

.service-card-wrapper:hover .service-card {
  @apply border-primary/50 shadow-lg;
  box-shadow: 0 10px 30px rgba(var(--primary) / 0.15);
}

.service-card-title {
  @apply flex items-center justify-between;
}

.service-card-arrow {
  @apply opacity-0 -translate-x-2 transition-all duration-300 text-primary;
}

.service-card-wrapper:hover .service-card-arrow {
  @apply opacity-100 translate-x-0;
}

.service-card-glow {
  @apply absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300 pointer-events-none;
}

.service-card-wrapper:hover .service-card-glow {
  @apply opacity-100;
}

.service-badge {
  @apply absolute -top-2 -right-3 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200;
  z-index: 10;
}
</style>
